/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useLocation} from 'react-router-dom'
import {
    AspectRatio,
    Box,
    Image
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '.'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {productUrlBuilder} from '../../utils/url'

/**
 * In the context of a cart product item variant, this component renders the item's
 * main image.
 *
 * @todo = This component will render a `SALE` badge when the qualifier is available
 * on the custom property `c_isSale`. This will need to be expanded upon to handle
 * different badge/qualifiers and property names.
 */
const ItemImage = ({imageProps, ratio = 1, isGiftCard = false, isFewItemsLeft = false, ...props}) => {
    const variant = useItemVariant()
    const location = useLocation()
    const isCheckout = /\/checkout$/.test(location?.pathname)
    const shouldBeClickable = !isGiftCard && !isCheckout

    // We find the 'small' images in the variant's image groups based on variationValues and pick the first one
    const image = findImageGroupBy(variant?.imageGroups, {
        viewType: 'small',
        selectedVariationAttributes: variant?.variationValues
    })?.images?.[0]

    const imageElement = (
        <AspectRatio ratio={ratio}>
            <Box position="relative" px={0}>
                {image && (
                    <Image
                        borderRadius={isGiftCard ? '8px' : '0'}
                        width={isGiftCard ? '75%' : '100%'}
                        overflow="hidden"
                        alt={image.alt}
                        src={`${image.disBaseLink || image.link}`}
                        ignoreFallback={true}
                        {...imageProps}
                    />
                )}
            </Box>
        </AspectRatio>
    )

    return (
        <Box width="84px" backgroundColor="webTheme.backgroundPrimaryBrand" {...props}>
            {shouldBeClickable ? (
                <Link to={productUrlBuilder(variant)} _hover={{textDecoration: 'none'}}>
                    {imageElement}
                </Link>
            ) : (
                imageElement
            )}
        </Box>
    )
}

ItemImage.propTypes = {
    imageProps: PropTypes.object,
    ratio: PropTypes.number,
    isGiftCard: PropTypes.bool,
    isFewItemsLeft: PropTypes.bool
}

export default ItemImage
